// Towing details panel on the order detail modal, shown for every booking with recoveryInfo.
// Pickup / drop-off, route, vehicle state, the provider's rate card and a planned-route
// estimate, live GPS trip progress, and (staff only) the card charge state.
// The final towing price is computed server-side on completion (GetRecoveryTripPrice in
// functions/helpers.js) from the recorded trip, so the estimate here mirrors that formula.
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { API_CONFIG, ORDER_COLLECTION } from '../../../../config/env';
import { firestore } from '../../../../helpers/Firebase';
import './ReassignmentPanel.css';
import './RecoveryInfoPanel.css';

const TZ = 'Asia/Dubai';
const MAPS_KEY = API_CONFIG.googleMapsApiKey || 'AIzaSyCExglJjHXgXzSGHJB1NmLll7NgjOG8_bc';
const MAX_PATH_POINTS = 60; // keeps the static map URL well under its length limit

const TYPE_LABEL = { flat: 'Flat', normal: 'Normal', box: 'Box' };
const CHARGE_STATE = {
  paid: { tone: 'success', label: 'Paid' },
  charging: { tone: 'warning', label: 'Charging' },
  failed: { tone: 'danger', label: 'Charge failed' },
  pending_verification: { tone: 'danger', label: 'Needs verification' },
};

const fmt = (ms) => (ms ? moment.tz(Number(ms), TZ).format('D MMM, HH:mm') : '—');
const hhmm = (ms) => moment.tz(Number(ms), TZ).format('HH:mm');
const aed = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? '—' : `AED ${Number(v).toFixed(2)}`);
const title = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '—');
const hasCoords = (p) => p && p.latitude != null && p.longitude != null;
const ll = (p) => `${p.latitude},${p.longitude}`;
const mapsLink = (p) => `https://www.google.com/maps/search/?api=1&query=${ll(p)}`;
const directionsLink = (a, b) => `https://www.google.com/maps/dir/?api=1&origin=${ll(a)}&destination=${ll(b)}&travelmode=driving`;

const rateBand = (pricing, distance) => (pricing?.distanceVariations || []).find(
  (b) => Number(distance) >= Number(b.minDistance) && Number(distance) <= Number(b.maxDistance),
);

const staticMapUrl = (pickup, drop, trail) => {
  const params = [
    'size=640x220', 'scale=2', 'maptype=roadmap',
    `markers=color:green%7Clabel:P%7C${ll(pickup)}`,
  ];
  if (hasCoords(drop)) params.push(`markers=color:red%7Clabel:D%7C${ll(drop)}`);
  if (trail.length > 1) {
    const step = Math.max(1, Math.ceil(trail.length / MAX_PATH_POINTS));
    const pts = trail.filter((_, i) => i % step === 0 || i === trail.length - 1);
    params.push(`path=color:0x3498dbcc%7Cweight:4%7C${pts.map(ll).join('%7C')}`);
  } else if (hasCoords(drop)) {
    params.push(`path=color:0x9ca3afaa%7Cweight:3%7C${ll(pickup)}%7C${ll(drop)}`);
  }
  return `https://maps.googleapis.com/maps/api/staticmap?${params.join('&')}&key=${MAPS_KEY}`;
};

const Stop = ({ tag, tone, label, place }) => (
  <div className="ri-stop">
    <span className={`ri-pin is-${tone}`}>{tag}</span>
    <div className="ri-stop-main">
      <div className="ri-stop-label">{label}</div>
      <div className="ri-stop-addr">{place?.address || '—'}</div>
      {hasCoords(place) && (
        <div className="ri-stop-meta">
          <span>{Number(place.latitude).toFixed(5)}, {Number(place.longitude).toFixed(5)}</span>
          <a href={mapsLink(place)} target="_blank" rel="noopener noreferrer">Open in Maps</a>
        </div>
      )}
    </div>
  </div>
);

const RecoveryInfoPanel = ({ booking, isStaff }) => {
  const info = booking && booking.recoveryInfo;
  const bookingId = booking && (booking.key || booking.id);
  const [trail, setTrail] = useState([]);

  // The worker app streams GPS points into tripCoordinates while towing; follow it live so
  // support can see whether the trip has started and where the vehicle is now.
  useEffect(() => {
    if (!info || !bookingId) return undefined;
    const unsub = firestore().collection(ORDER_COLLECTION).doc(bookingId).collection('tripCoordinates')
      .onSnapshot(
        (snap) => setTrail(snap.docs
          .map((d) => {
            const { coords, timestamp } = d.data() || {};
            return coords ? { latitude: coords.latitude, longitude: coords.longitude, timestamp } : null;
          })
          .filter(hasCoords)
          .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))),
        () => setTrail([]),
      );
    return () => unsub();
  }, [info, bookingId]);

  if (!info) return null;

  const { pickupLocation: pickup, dropLocation: drop, recoveryPricing: pricing } = info;
  const distance = Number(info.distance);
  const hasDistance = Number.isFinite(distance) && distance > 0;
  const notDrivable = info.vehicleDrivable === 'no';
  const isSos = info.scheduleType === 'sos';

  const base = Number(pricing?.[info.recoveryType] || 0);
  const band = hasDistance ? rateBand(pricing, distance) : null;
  const perKm = Number(band?.priceRate || 0);
  const emergency = notDrivable ? Number(pricing?.emergency || 0) : 0;
  const estimate = base + perKm * (hasDistance ? distance : 0) + emergency;

  const charge = booking.recoveryCharge;
  const chargeState = charge && (CHARGE_STATE[charge.state] || { tone: 'secondary', label: title(charge.state) });
  const last = trail[trail.length - 1];

  return (
    <div className="rp-card ri-card">
      <div className="rp-head">
        <div className="rp-title">
          <h6>Towing details</h6>
          <span className={`rp-state is-${isSos ? 'danger' : 'info'}`}><i className="rp-dot" />{isSos ? 'SOS — immediate' : 'Scheduled'}</span>
          <span className={`rp-state is-${notDrivable ? 'warning' : 'success'}`}>{notDrivable ? 'Vehicle not drivable' : 'Vehicle drivable'}</span>
        </div>
        {hasCoords(pickup) && hasCoords(drop) && (
          <a className="btn btn-outline-primary btn-xs" href={directionsLink(pickup, drop)} target="_blank" rel="noopener noreferrer">
            Directions
          </a>
        )}
      </div>

      <div className="rp-body">
        <div className="ri-route">
          <div className="ri-stops">
            <Stop tag="P" tone="success" label="Pickup" place={pickup} />
            <div className="ri-leg">
              {hasDistance ? `${distance.toFixed(1)} km` : '—'}{info.duration ? ` · ~${info.duration}` : ''}
            </div>
            <Stop tag="D" tone="danger" label="Drop-off" place={drop} />
          </div>
          {hasCoords(pickup) && (
            <a className="ri-map" href={hasCoords(drop) ? directionsLink(pickup, drop) : mapsLink(pickup)} target="_blank" rel="noopener noreferrer">
              <img alt="Towing route" src={staticMapUrl(pickup, drop, trail)} />
            </a>
          )}
        </div>

        <div className="rp-section">
          <div className="rp-section-title">Trip</div>
          <dl className="rp-meta">
            <dt>Towing type</dt><dd>{TYPE_LABEL[info.recoveryType] || title(info.recoveryType)}</dd>
            <dt>Planned route</dt><dd>{hasDistance ? `${distance.toFixed(1)} km` : '—'}{info.duration ? `, about ${info.duration}` : ''}</dd>
            <dt>GPS tracking</dt>
            <dd>
              {trail.length
                ? <>{trail.length} points, {fmt(trail[0].timestamp)} → {fmt(last.timestamp)}
                  {' '}<a href={mapsLink(last)} target="_blank" rel="noopener noreferrer">Last position</a></>
                : 'Not started — no GPS points from the worker yet'}
            </dd>
          </dl>
        </div>

        {pricing && (
          <div className="rp-section">
            <div className="rp-section-title">Price estimate (planned route)</div>
            <dl className="rp-meta">
              <dt>Base ({TYPE_LABEL[info.recoveryType] || title(info.recoveryType)})</dt><dd>{aed(base)}</dd>
              <dt>Distance</dt>
              <dd>{hasDistance && band ? `${distance.toFixed(1)} km × AED ${perKm}/km = ${aed(perKm * distance)}` : '—'}</dd>
              {notDrivable && <><dt>Not drivable</dt><dd>{aed(emergency)}</dd></>}
              <dt>Estimate</dt><dd><b>{aed(estimate)}</b></dd>
              {pricing.rushHours?.length ? (
                <>
                  <dt>Rush hours</dt>
                  <dd>{pricing.rushHours.map((r) => `${hhmm(r.startTime)}–${hhmm(r.endTime)} (AED ${r.priceRate}/km)`).join(', ')}</dd>
                </>
              ) : null}
            </dl>
            <div className="ri-note">
              The final price is calculated when the worker completes the order, from the GPS trip and the rush-hour rate at that time.
            </div>
          </div>
        )}

        {isStaff && (charge || booking.paymentMethod?.paymentLink) && (
          <div className="rp-section">
            <div className="rp-section-title">Card charge</div>
            <dl className="rp-meta">
              {chargeState && <><dt>State</dt><dd><span className={`rp-state is-${chargeState.tone}`}><i className="rp-dot" />{chargeState.label}</span></dd></>}
              {charge?.amount != null && <><dt>Charged</dt><dd>{aed(charge.amount)}</dd></>}
              {charge?.paidAt && <><dt>Paid at</dt><dd>{fmt(charge.paidAt)}</dd></>}
              {charge?.attempts ? <><dt>Attempts</dt><dd>{charge.attempts}</dd></> : null}
              {charge?.lastState && <><dt>Gateway state</dt><dd>{charge.lastState}</dd></>}
              {charge?.orderReference && <><dt>Reference</dt><dd className="ri-mono">{charge.orderReference}</dd></>}
              {booking.paymentMethod?.paymentLink && (
                <><dt>Payment link</dt><dd><a href={booking.paymentMethod.paymentLink} target="_blank" rel="noopener noreferrer">Open link</a></dd></>
              )}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecoveryInfoPanel;
